import React, { useState, useEffect, useContext, useRef } from 'react';
import AppContext from '../../contexts/AppContext';
import { MessageCircle, Trophy, X, Send, Users, Image, Edit, Trash2, Archive } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import { uploadMessageMedia } from '../../services/storageService';
import SecurityUtils from '../../utils/security';
import { validateVideoDuration, fixVideoOrientation } from '../../utils/media';
import Avatar from '../../components/common/Avatar';

const MessagesView = () => {
  const { conversations, setConversations, messages, setMessages, users, currentUser, setView, showAlert } = useContext(AppContext);
  const [selectedConv, setSelectedConv] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [editingMsg, setEditingMsg] = useState(null);
  const [editMsgContent, setEditMsgContent] = useState('');
  const [confirmDeleteConv, setConfirmDeleteConv] = useState(null);
  const [confirmArchiveConv, setConfirmArchiveConv] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [sendingMedia, setSendingMedia] = useState(false);
  const [viewMedia, setViewMedia] = useState(null);
  const mediaInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Load messages in real-time when a conversation is selected
  useEffect(() => {
    if (!selectedConv) return;

    const unsub = firestoreService.onMessagesChange(selectedConv.id, (convMsgs) => {
      setMessages(prev => {
        // Replace messages for this conversation, keep others
        const otherMsgs = prev.filter(m => m.conversationId !== selectedConv.id);
        return [...otherMsgs, ...convMsgs];
      });
      // Mark as read whenever new messages arrive while viewing this conversation
      firestoreService.markConversationAsRead(selectedConv.id, currentUser.id).catch(() => {});
    });

    // Mark unread messages as read on initial open
    firestoreService.markConversationAsRead(selectedConv.id, currentUser.id).catch(() => {});

    return () => unsub();
  }, [selectedConv, currentUser.id, setMessages]);

  // Scroll to bottom when entering a chat or when messages change
  const convMessageCount = selectedConv ? messages.filter(m => m.conversationId === selectedConv.id).length : 0;
  useEffect(() => {
    if (selectedConv && convMessageCount > 0) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 100);
    }
  }, [convMessageCount, selectedConv]);

  const getOtherUser = (conv) => {
    const otherId = conv.participants.find(id => id !== currentUser.id);
    return users.find(u => u.id === otherId);
  };

  const getGroupMembers = (conv) => {
    return conv.participants
      .filter(id => id !== currentUser.id)
      .map(id => users.find(u => u.id === id))
      .filter(Boolean);
  };

  const getConvDisplayName = (conv) => {
    if (conv.isGroup) return conv.name;
    const other = getOtherUser(conv);
    return other?.name || 'Unknown';
  };

  const getUnreadCount = (conv) => {
    return messages.filter(m => m.conversationId === conv.id && m.senderId !== currentUser.id && !m.read).length;
  };

  const handleMediaSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isVideo = file.type.startsWith('video/');
    const validation = SecurityUtils.validateFile(file, { maxSize: isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024 });
    if (!validation.valid) { showAlert(`File validation failed: ${validation.error}`, 'Error'); return; }
    if (validation.type !== 'image' && validation.type !== 'video') { showAlert('Please select an image or video file.', 'Error'); return; }
    if (isVideo) {
      const validDuration = await validateVideoDuration(file, 15);
      if (!validDuration) { showAlert('Video must be 15 seconds or shorter.', 'Error'); return; }
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setMediaFile(file);
      setMediaPreview(reader.result);
      setMediaType(isVideo ? 'video' : 'image');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const clearMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType(null);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !mediaFile) return;
    if (!selectedConv) return;
    const sanitizedMessage = newMessage.trim() ? SecurityUtils.sanitizeText(newMessage) : '';

    setNewMessage('');
    const currentMediaFile = mediaFile;
    const currentMediaType = mediaType;
    clearMedia();

    try {
      setSendingMedia(!!currentMediaFile);
      let mediaUrl = null;
      if (currentMediaFile) {
        const fileToUpload = currentMediaType === 'video' ? await fixVideoOrientation(currentMediaFile) : currentMediaFile;
        const result = await uploadMessageMedia(fileToUpload, currentUser.id, selectedConv.id);
        mediaUrl = result.url;
      }
      await firestoreService.sendMessage({
        conversationId: selectedConv.id,
        senderId: currentUser.id,
        content: sanitizedMessage,
        ...(mediaUrl ? { mediaUrl, mediaType: currentMediaType } : {}),
      });
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSendingMedia(false);
    }
  };

  const canEditMessage = (msg) => {
    if (msg.senderId !== currentUser.id) return false;
    const msgTime = new Date(msg.timestamp).getTime();
    const now = Date.now();
    return (now - msgTime) < 3 * 60 * 1000; // 3 minutes
  };

  const handleEditMessage = async () => {
    if (!editMsgContent.trim() || !editingMsg) return;
    const sanitized = SecurityUtils.sanitizeText(editMsgContent.trim());

    // Optimistic update
    setMessages(prev => prev.map(m =>
      m.id === editingMsg.id ? { ...m, content: sanitized, edited: true } : m
    ));
    setEditingMsg(null);
    setEditMsgContent('');

    try {
      await firestoreService.updateMessage(editingMsg.id, { content: sanitized, edited: true });
    } catch (err) {
      console.error('Error editing message:', err);
    }
  };

  const handleDeleteMessage = async (msgId) => {
    // Mark as deleted instead of removing
    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, deleted: true, content: 'Message deleted' } : m));
    try {
      await firestoreService.updateMessage(msgId, { deleted: true, content: 'Message deleted' });
    } catch (err) {
      console.error('Error deleting message:', err);
    }
  };

  const handleDeleteConversation = async (convId) => {
    try {
      // Remove self from participants instead of deleting for everyone
      await firestoreService.removeParticipant(convId, currentUser.id);
      setConversations(prev => prev.filter(c => c.id !== convId));
      setMessages(prev => prev.filter(m => m.conversationId !== convId));
      if (selectedConv?.id === convId) setSelectedConv(null);
      setConfirmDeleteConv(null);
    } catch (err) {
      console.error('Error deleting conversation:', err);
    }
  };

  const handleArchiveConversation = async (convId) => {
    try {
      await firestoreService.archiveConversation(convId, currentUser.id);
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, archivedBy: [...(c.archivedBy || []), currentUser.id] } : c));
      if (selectedConv?.id === convId) setSelectedConv(null);
      setConfirmArchiveConv(null);
    } catch (err) {
      console.error('Error archiving conversation:', err);
    }
  };

  const handleUnarchiveConversation = async (convId) => {
    try {
      await firestoreService.unarchiveConversation(convId, currentUser.id);
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, archivedBy: (c.archivedBy || []).filter(id => id !== currentUser.id) } : c));
    } catch (err) {
      console.error('Error unarchiving conversation:', err);
    }
  };

  const convMessages = selectedConv ? messages.filter(m => m.conversationId === selectedConv.id) : [];
  const allUserConversations = conversations
    .filter(conv => conv.participants.includes(currentUser.id) && !conv.linkedGroupId && !conv.linkedEventId);
  const activeConversations = allUserConversations
    .filter(conv => !conv.archivedBy?.includes(currentUser.id))
    .sort((a, b) => new Date(b.lastMessageTime || 0) - new Date(a.lastMessageTime || 0));
  const archivedConversations = allUserConversations
    .filter(conv => conv.archivedBy?.includes(currentUser.id))
    .sort((a, b) => new Date(b.lastMessageTime || 0) - new Date(a.lastMessageTime || 0));
  const userConversations = showArchived ? archivedConversations : activeConversations;

  const conversationListJSX = (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-700">
        <h2 className="font-bold text-white text-lg mb-2">Messages</h2>
        <div className="flex space-x-2">
          <button onClick={() => setShowArchived(false)} className={`px-3 py-1 text-xs rounded-full font-medium ${!showArchived ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
            Active{activeConversations.length > 0 && ` (${activeConversations.length})`}
          </button>
          <button onClick={() => setShowArchived(true)} className={`px-3 py-1 text-xs rounded-full font-medium flex items-center space-x-1 ${showArchived ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
            <Archive className="w-3 h-3" />
            <span>Archived{archivedConversations.length > 0 && ` (${archivedConversations.length})`}</span>
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto divide-y divide-gray-800">
        {userConversations.length === 0 ? (
          <div className="p-8 text-center">
            <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-500" />
            <p className="text-gray-400">{showArchived ? 'No archived conversations' : 'No conversations yet'}</p>
          </div>
        ) : (
          userConversations.map(conv => {
            const other = getOtherUser(conv);
            const unread = getUnreadCount(conv);
            return (
              <div key={conv.id} className="relative group">
                {confirmDeleteConv === conv.id ? (
                  <div className="p-3 bg-gray-900 flex items-center justify-between">
                    <p className="text-sm text-white">Delete this conversation?</p>
                    <div className="flex space-x-2">
                      <button onClick={() => setConfirmDeleteConv(null)} className="px-2 py-1 text-xs text-white bg-gray-700 rounded hover:bg-gray-600">Cancel</button>
                      <button onClick={() => handleDeleteConversation(conv.id)} className="px-2 py-1 text-xs text-pure-white bg-red-600 rounded hover:bg-red-700">Delete</button>
                    </div>
                  </div>
                ) : confirmArchiveConv === conv.id ? (
                  <div className="p-3 bg-gray-900 flex items-center justify-between">
                    <p className="text-sm text-white">Archive this conversation?</p>
                    <div className="flex space-x-2">
                      <button onClick={() => setConfirmArchiveConv(null)} className="px-2 py-1 text-xs text-white bg-gray-700 rounded hover:bg-gray-600">Cancel</button>
                      <button onClick={() => handleArchiveConversation(conv.id)} className="px-2 py-1 text-xs text-pure-white bg-blue-600 rounded hover:bg-blue-700">Archive</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex">
                    <button
                      onClick={() => setSelectedConv(conv)}
                      className={`flex-1 p-3 text-left hover:bg-gray-900 transition-colors ${selectedConv?.id === conv.id ? 'bg-gray-800' : ''}`}
                    >
                      <div className="flex items-center space-x-3">
                        {conv.isGroup ? (
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-pure-white font-medium flex-shrink-0 ${conv.linkedEventId ? 'bg-purple-600' : 'bg-[#16a34a]'}`}>
                            {conv.linkedEventId ? <Trophy className="w-5 h-5" /> : <Users className="w-5 h-5" />}
                          </div>
                        ) : (
                          <div className="flex-shrink-0"><Avatar user={other} size="md" /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-white truncate text-sm">{getConvDisplayName(conv)}</p>
                            {unread > 0 && (
                              <span className="ml-2 bg-[#16a34a] text-pure-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">{unread}</span>
                            )}
                          </div>
                          {conv.isGroup ? (
                            <span className="text-xs text-gray-400">{conv.participants.length} members</span>
                          ) : (
                            <span className="text-xs text-gray-400">@{other?.handle}</span>
                          )}
                          <p className="text-xs text-gray-300 truncate mt-0.5">{conv.lastMessage}</p>
                        </div>
                      </div>
                    </button>
                    <div className="flex flex-col justify-center opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      {showArchived ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleUnarchiveConversation(conv.id); }}
                          className="px-2 py-1 text-gray-400 hover:text-blue-400"
                          title="Unarchive conversation"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmArchiveConv(conv.id); }}
                          className="px-2 py-1 text-gray-400 hover:text-blue-400"
                          title="Archive conversation"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteConv(conv.id); }}
                        className="px-2 py-1 text-gray-400 hover:text-red-500"
                        title="Delete conversation"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  const chatPanelJSX = selectedConv ? (
    <div className="flex flex-col h-full">
      {/* Chat header */}
      <div className="p-3 border-b border-gray-700 flex items-center space-x-3">
        <button
          onClick={() => setSelectedConv(null)}
          className="md:hidden p-1 text-white hover:text-gray-300 flex-shrink-0"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        </button>
        {selectedConv.isGroup ? (
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-pure-white font-medium flex-shrink-0 ${selectedConv.linkedEventId ? 'bg-purple-600' : 'bg-[#16a34a]'}`}>
            {selectedConv.linkedEventId ? <Trophy className="w-5 h-5" /> : <Users className="w-5 h-5" />}
          </div>
        ) : (
          <div className="flex-shrink-0"><Avatar user={getOtherUser(selectedConv)} size="md" /></div>
        )}
        <div className="min-w-0">
          <p className="font-medium text-white truncate">{getConvDisplayName(selectedConv)}</p>
          {selectedConv.isGroup ? (
            <p className="text-xs text-gray-400 truncate">{getGroupMembers(selectedConv).map(u => u.name).join(', ')}</p>
          ) : (
            <p className="text-xs text-gray-400">@{getOtherUser(selectedConv)?.handle}</p>
          )}
        </div>
      </div>

      {/* Messages area */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[rgb(var(--color-bg-primary))]">
        {convMessages.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            <p className="text-sm">No messages yet. Say hello!</p>
          </div>
        )}
        {convMessages.map(msg => {
          // System messages (e.g. "Event was deleted by the owner.")
          if (msg.senderId === 'system') {
            return (
              <div key={msg.id} className="flex justify-center my-2">
                <p className="text-xs text-gray-400 bg-gray-800/50 px-3 py-1 rounded-full italic">{msg.content}</p>
              </div>
            );
          }
          const isMine = msg.senderId === currentUser.id;
          const isDeleted = msg.deleted;
          const isSharedPost = msg.sharedPostId;
          const isSharedEvent = msg.sharedEventId;
          const sender = users.find(u => u.id === msg.senderId);
          const senderName = selectedConv.isGroup ? (isMine ? null : sender?.name) : null;
          const editable = canEditMessage(msg) && !isDeleted;
          const isEditing = editingMsg?.id === msg.id;
          return (
            <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'} group`}>
              {isMine && !isEditing && !isDeleted && (
                <div className="self-center mr-1 flex space-x-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  {editable && (
                    <button
                      onClick={() => { setEditingMsg(msg); setEditMsgContent(msg.content); }}
                      className="p-1 text-gray-500 hover:text-white"
                      title="Edit message"
                    >
                      <Edit className="w-3 h-3" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteMessage(msg.id)}
                    className="p-1 text-gray-500 hover:text-red-500"
                    title="Delete message"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              )}
              {!isMine && <div className="flex-shrink-0 self-start mt-1 mr-1"><Avatar user={sender} size="xs" /></div>}
              {isDeleted ? (
                <div className={`max-w-[75%] bg-gray-800/50 rounded-2xl px-2 py-1`}>
                  <p className="text-sm italic text-red-400/70">Message deleted</p>
                </div>
              ) : (
              <div className={`max-w-[75%] ${isMine ? 'bg-[#16a34a] text-pure-white' : 'bg-[rgb(var(--color-bg-tertiary))] text-[rgb(var(--color-text-primary))]'} rounded-2xl px-2 py-1`}>
                {senderName && <p className="text-xs font-bold mb-1 text-blue-200">{senderName}</p>}
                {isEditing ? (
                  <div>
                    <input
                      type="text"
                      value={editMsgContent}
                      onChange={(e) => setEditMsgContent(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleEditMessage()}
                      className="w-full px-2 py-1 text-sm bg-black/30 text-white rounded border border-white/30 focus:outline-none focus:border-white"
                      autoFocus
                    />
                    <div className="flex justify-end space-x-2 mt-1">
                      <button onClick={() => { setEditingMsg(null); setEditMsgContent(''); }} className="text-xs opacity-70 hover:opacity-100">Cancel</button>
                      <button onClick={handleEditMessage} className="text-xs font-bold hover:opacity-80">Save</button>
                    </div>
                  </div>
                ) : (
                  <>
                    {msg.mediaUrl && msg.mediaType === 'image' && (
                      <img src={msg.mediaUrl} alt="" className="rounded-lg max-w-full max-h-60 mb-1 cursor-pointer" onClick={() => setViewMedia({ url: msg.mediaUrl, type: 'image' })} />
                    )}
                    {msg.mediaUrl && msg.mediaType === 'video' && (
                      <video src={msg.mediaUrl} controls playsInline className="rounded-lg max-w-full max-h-60 mb-1 cursor-pointer" onClick={(e) => { e.preventDefault(); setViewMedia({ url: msg.mediaUrl, type: 'video' }); }} />
                    )}
                    {msg.content && <p className="text-sm break-words">{msg.content}</p>}
                    {isSharedPost && (
                      <div className="mt-2 p-2 bg-black bg-opacity-30 rounded text-xs">
                        <p className="font-medium">Post from {msg.sharedPostAuthor}</p>
                        <a
                          href={`#post-${msg.sharedPostId}`}
                          onClick={(e) => {
                            e.preventDefault();
                            setView('feed');
                            setTimeout(() => {
                              const element = document.getElementById(`post-${msg.sharedPostId}`);
                              if (element) {
                                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                element.classList.add('ring-2', 'ring-[#16a34a]');
                                setTimeout(() => element.classList.remove('ring-2', 'ring-[#16a34a]'), 2000);
                              }
                            }, 100);
                          }}
                          className="text-white hover:underline mt-1 block"
                        >
                          "{msg.sharedPostPreview}"
                        </a>
                      </div>
                    )}
                    {isSharedEvent && (
                      <a
                        href={`#event-${msg.sharedEventId}`}
                        onClick={(e) => {
                          e.preventDefault();
                          setView(`event-detail-${msg.sharedEventId}`);
                        }}
                        className="mt-2 p-2 bg-black bg-opacity-30 rounded text-xs block hover:bg-opacity-50"
                      >
                        <p className="font-medium flex items-center space-x-1">
                          <Trophy className="w-3 h-3" />
                          <span>Event</span>
                        </p>
                        {msg.sharedEventTitle && <p className="font-bold mt-1">{msg.sharedEventTitle}</p>}
                        {(msg.sharedEventDate || msg.sharedEventCourse) && (
                          <p className="opacity-80">
                            {msg.sharedEventDate}{msg.sharedEventDate && msg.sharedEventCourse ? ' · ' : ''}{msg.sharedEventCourse}
                          </p>
                        )}
                        <p className="underline mt-1">View event →</p>
                      </a>
                    )}
                    <p className="text-xs mt-1 opacity-70">
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {msg.edited && <span className="ml-1 italic">(edited)</span>}
                    </p>
                  </>
                )}
              </div>
              )}
              {isMine && <div className="flex-shrink-0 self-start mt-1 ml-1"><Avatar user={currentUser} size="xs" /></div>}
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Message input */}
      <div className="p-3 border-t border-gray-700 overflow-hidden">
        {selectedConv.disabled ? (
          <p className="text-center text-gray-500 text-sm py-2">This chat has been disabled.</p>
        ) : (
          <div>
            {mediaPreview && (
              <div className="mb-2 relative inline-block">
                {mediaType === 'image' ? (
                  <img src={mediaPreview} alt="Preview" className="max-h-32 rounded-lg" />
                ) : (
                  <video src={mediaPreview} className="max-h-32 rounded-lg" controls playsInline />
                )}
                <button onClick={clearMedia} className="absolute -top-2 -right-2 bg-red-600 text-pure-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-700">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            {sendingMedia && (
              <p className="text-xs text-gray-400 mb-1">Uploading media...</p>
            )}
            <div className="flex space-x-2">
              <button onClick={() => mediaInputRef.current?.click()} className="p-3 text-gray-400 hover:text-white flex-shrink-0" title="Attach photo or video">
                <Image className="w-5 h-5" />
              </button>
              <input ref={mediaInputRef} type="file" accept="image/*,video/*" onChange={handleMediaSelect} className="hidden" />
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !sendingMedia && handleSendMessage()}
                placeholder="Type a message..."
                className="flex-1 min-w-0 px-4 py-3 border border-gray-700 rounded-full focus:ring-2 focus:ring-[#22c55e] text-white bg-black text-sm"
              />
              <button
                onClick={handleSendMessage}
                disabled={(!newMessage.trim() && !mediaFile) || sendingMedia}
                className="p-3 bg-[#16a34a] text-pure-white rounded-full hover:bg-[#15803d] disabled:opacity-50 flex-shrink-0"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className="max-w-4xl mx-auto h-full">
      {/* Mobile layout: show one or the other */}
      <div className="md:hidden h-full bg-black rounded-lg shadow border border-gray-700 overflow-hidden">
        {selectedConv ? chatPanelJSX : conversationListJSX}
      </div>

      {/* Desktop layout: side by side */}
      <div className="hidden md:flex h-full gap-4">
        <div className="w-80 bg-black rounded-lg shadow border border-gray-700 overflow-hidden flex-shrink-0">
          {conversationListJSX}
        </div>
        <div className="flex-1 bg-black rounded-lg shadow border border-gray-700 overflow-hidden">
          {chatPanelJSX || (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4" />
                <p>Select a conversation</p>
              </div>
            </div>
          )}
        </div>
      </div>
      {viewMedia && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setViewMedia(null)}>
          <button onClick={() => setViewMedia(null)} className="absolute top-4 right-4 text-white hover:text-gray-300 z-50">
            <X className="w-8 h-8" />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full">
            {viewMedia.type === 'image' ? (
              <img src={viewMedia.url} alt="" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
            ) : (
              <video src={viewMedia.url} controls autoPlay playsInline className="max-w-full max-h-[90vh] rounded-lg" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesView;
